import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/kedve/Reveal";

const STORIES = [
  {
    name: "Priya S.",
    from: "Product Manager",
    to: "Head of Product",
    quote: "kedve rebuilt the way I show up in an executive room. Six months later, my CEO asked me to run product strategy.",
  },
  {
    name: "Marcus D.",
    from: "Senior Engineer",
    to: "Engineering Director",
    quote: "I stopped studying content and started rehearsing conversations. That is the entire difference.",
  },
  {
    name: "Aya K.",
    from: "Account Manager",
    to: "Strategic Accounts Lead",
    quote: "kedve told me what I already suspected about my career, and then gave me the sequence to change it.",
  },
];

export default function Philosophy() {
  return (
    <div data-testid="page-philosophy" className="text-black">
      {/* HERO */}
      <section className="pt-36 pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="eyebrow opacity-60">Our Philosophy</div>
          <h1 className="mt-8 font-serif text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-tightest">
            A career is<br /><span className="italic">not a curriculum.</span>
          </h1>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="pb-28 md:pb-40 border-t border-black/15">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow opacity-60">A Founder Letter</div>
            <div className="mt-6 font-serif text-2xl italic">— from the kedve Studio</div>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-8 text-lg md:text-xl leading-[1.6] font-serif">
            <p>kedve began with a question we could not answer with more content.</p>
            <p>Why do so many talented, hard-working professionals plateau at the exact moment they should accelerate?</p>
            <p className="italic">Because a career is not a curriculum.</p>
            <p>What separates a senior professional from a leader is not more knowledge. It is a different way of working, a different set of habits, a different room they can hold.</p>
            <p>kedve is not a shortcut. It is the opposite. A slow, deliberate way of becoming the professional the business already believes you should be.</p>
            <p>We do not sell certificates. We sell the quiet difference between the person who enters the room and the person who owns it.</p>
          </div>
        </div>
      </section>

      {/* CAREER STORIES */}
      <section id="stories" className="py-28 md:py-40 bg-neutral-50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="eyebrow opacity-60">Career Stories</div>
          <h2 className="mt-6 font-serif text-5xl md:text-7xl tracking-tightest leading-[0.92] max-w-4xl">The people this<br /><span className="italic">was built for.</span></h2>

          <div className="mt-20 space-y-16">
            {STORIES.map((s, i) => (
              <Reveal key={s.name} delay={i * 80}>
                <div className="grid md:grid-cols-12 gap-8 border-t border-black/20 pt-10" data-testid={`story-${i}`}>
                  <div className="md:col-span-3">
                    <div className="font-mono text-xs opacity-60">Story · 0{i + 1}</div>
                    <div className="mt-4 font-serif text-2xl">{s.name}</div>
                    <div className="mt-2 text-sm opacity-70">{s.from} <span className="italic opacity-50">to</span> {s.to}</div>
                  </div>
                  <blockquote className="md:col-span-9 font-serif text-3xl md:text-4xl leading-[1.15] tracking-tight italic opacity-90">
                    &ldquo;{s.quote}&rdquo;
                  </blockquote>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-5xl md:text-7xl tracking-tightest leading-[0.9]">Ready for the<br /><span className="italic">next version of you?</span></h2>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/apply" data-testid="philosophy-apply" className="inline-flex items-center gap-2 bg-black text-white rounded-full px-8 py-4 text-sm tracking-wide hover:bg-black/85 transition">
              Apply <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/accelerators" data-testid="philosophy-accelerators" className="inline-flex items-center gap-2 border border-black rounded-full px-8 py-4 text-sm tracking-wide hover:bg-black hover:text-white transition">
              See the programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
